import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Film, Camera, ChevronRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VenuesIndex = () => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Essex Wedding Venues We Film & Photograph | Wedding Video Essex';
    let el = document.querySelector('meta[name="description"]');
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'description');
      document.head.appendChild(el);
    }
    el.setAttribute(
      'content',
      'Real wedding films and photography from 34+ Essex and UK wedding venues. Explore our venue-by-venue galleries — Crondon Park, Leez Priory, Gaynes Park, Braxted Park and more.',
    );

    const fetchVenues = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/venues`);
        const d = await r.json();
        setVenues(d.venues || []);
      } catch {
        setVenues([]);
      } finally {
        setLoading(false);
      }
    };
    fetchVenues();
  }, []);

  return (
    <div data-testid="venues-index" style={{ backgroundColor: '#1A1A1A' }}>
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-[#1A1A1A]" />
        <div className="relative container mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold/10 border border-gold/30 mb-6">
            <MapPin className="w-4 h-4 text-gold" />
            <span className="text-gold text-xs font-semibold uppercase tracking-widest">Real weddings, real venues</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight">
            Wedding venues we've filmed &amp; photographed
          </h1>
          <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Explore real cinematic wedding films and photography from the venues we know inside out.
            Click any venue for the full gallery.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="px-4 sm:px-6 lg:px-8 pb-24">
        <div className="container mx-auto max-w-6xl">
          {loading ? (
            <div className="text-center py-24 text-white/50">Loading venues…</div>
          ) : venues.length === 0 ? (
            <div className="text-center py-24 text-white/50">No venues available right now.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {venues.map((v) => (
                <Link
                  key={v.slug}
                  to={`/venues/${v.slug}`}
                  data-testid={`venue-card-${v.slug}`}
                  className="group block bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-gold/40 transition-all"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-black relative">
                    {v.hero_image ? (
                      <img
                        src={v.hero_image}
                        alt={`${v.name} wedding films and photography`}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/20 text-xs">No image</div>
                    )}
                    <div className="absolute top-3 right-3 flex gap-1.5">
                      {v.video_count > 0 && (
                        <span className="bg-black/70 text-white text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full flex items-center gap-1">
                          <Film className="w-3 h-3" /> {v.video_count}
                        </span>
                      )}
                      {v.photo_count > 0 && (
                        <span className="bg-black/70 text-white text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full flex items-center gap-1">
                          <Camera className="w-3 h-3" /> {v.photo_count}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-serif text-xl text-white group-hover:text-gold transition-colors mb-1">
                      {v.name}
                    </h3>
                    {v.location && (
                      <p className="text-white/50 text-xs uppercase tracking-widest flex items-center gap-1.5">
                        <MapPin className="w-3 h-3" /> {v.location}
                      </p>
                    )}
                    <p className="text-white/60 text-sm mt-3 line-clamp-2 leading-relaxed">
                      {v.description}
                    </p>
                    <p className="mt-4 text-gold text-xs uppercase tracking-widest font-semibold flex items-center gap-1">
                      Explore {v.name} <ChevronRight className="w-3 h-3" />
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VenuesIndex;
